<template>
    <div
	ref="overflowWrapperRef"
	class="overflow-hidden-wrapper"
    v-touch:swipe.left="scrollLeft"
    v-touch:swipe.right="scrollRight"
	:class="{
		'bigger-slider': dashboardWidgetAnchor,
		'medium-slider': dashboardWidget,
		'is-company-session': isCompanySession,
		'no-before': hidePreviousCards
	}">
        <div v-if="!isCompanySession && (dashboardWidgetAnchor || dashboardWidget)" class="button-add" @click="emit('showNewCardModal')">
            <button><icon-plus/></button>
            <label>{{t('cards.consult.addButtonSlider')}}</label>
        </div>
        <!-- Arrow Left -->
		<transition name="appear">
			<div v-if="selectedCardIndex > 0 && !(getMobileAppState && (dashboardWidget || dashboardWidgetAnchor))" class="icon-wrapper left">
				<!-- Web Button -->
				<buttons-quaternary
                icon="arrow-left"
                class="icon"
                @clicked="emit('clicked', cardsList[selectedCardIndex - 1].id)"/>
			</div>
		</transition>
        <div class="card-slider-container">
            <div
            class="card-slider"
            :style="translateCss">
                <card-bank
                v-for="(card, index) in cardsList"
                class="card-element"
                :hideBalance="hideBalance"
				:dashboardWidget="dashboardWidget"
				:dashboardWidgetAnchor="dashboardWidgetAnchor"
                :key="`cardSliderElement${card.id}`"
                :hideMoreOptions="card.hideMoreOptions"
                :params="card"
                :selectedCardId="selectedCardId"
                :previousCard="index <= selectedCardIndex - 1"
                :nextCard="index === selectedCardIndex + 1"
				:hidePreviousCards="hidePreviousCards"
                @clicked="!getMobileAppState ? emit('clicked', $event) : null"/>
            </div>
        </div>
		<!-- Arrow Right -->
		<transition name="appear">
			<div v-if="selectedCardIndex < cardsList.length-1 && !(getMobileAppState && (dashboardWidget || dashboardWidgetAnchor))" class="icon-wrapper right">
				<!-- Web Button -->
				<buttons-quaternary
                icon="arrow-right"
                class="icon"
                @clicked="emit('clicked', cardsList[selectedCardIndex + 1].id)"/>
			</div>
		</transition>
        <!-- Circles to visually see our position in slider -->
        <navigation-circles-wrapper
        v-if="cardsList.length > 1"
        :listLength="cardsList.length"
        :selectedIndex="selectedCardIndex"/>
    </div>
</template>

<script setup>
import IconPlus from '@/assets/icons/plus.svg?inline'
import ButtonsQuaternary from '@/components/generic/buttons/Quaternary'
import CardBank from '@/components/generic/cards/Bank'
import NavigationCirclesWrapper from '@/components/generic/sliders/navigation/CirclesWrapper.vue'

/* Vue */
import { computed, ref, onMounted, onBeforeUnmount } from 'vue'

/* Store */
import { mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['clicked', 'showNewCardModal'])

/* mapGetters */
const { getAccountGroups } = mapGetters(moduleMapper.SESSION)
const { getMobileAppState } = mapGetters()

/* Props
@dashboardWidget {Boolean} - prop to apply logics for this to be used on dashboardWidget
@dashboardWidgetAnchor {Boolean} - prop to apply logics for this to be used on dashboardWidget Ancgor Variant
@selectedCardId {Number} - card id of selected card
@cardList {Array}
@hideBalance {Boolean} - flag to hide/show image with balances
*/
const props = defineProps({
	dashboardWidget: Boolean,
	dashboardWidgetAnchor: Boolean,
	selectedCardId: Number,
	cardsList: Array,
    hideBalance: Boolean
})

const isCompanySession = computed(() => {
    return getAccountGroups.value.find(obj => obj.default).type.toLowerCase() === 'company'
})

const selectedCardIndex = computed(() => {
    return props.cardsList.findIndex(obj => obj.id === props.selectedCardId)
})

const scrollLeft = () => {
    if (!getMobileAppState.value) return // We only use this when in mobile app

    let cardIndex = props.cardsList.indexOf(props.cardsList.find(card => card.id === props.selectedCardId))
    /* NOTE - By some black magic or some unexplained divine wonder this only works if you have this console log. We need to revisite this later */
    console.log(props.cardsList[cardIndex++].id, 'scrollLeft')

    emit('clicked', props.cardsList[cardIndex++].id)
}

const scrollRight = () => {
    if (!getMobileAppState.value) return // We only use this when in mobile app

    let cardIndex = props.cardsList.indexOf(props.cardsList.find(card => card.id === props.selectedCardId))
    /* NOTE - By some black magic or some unexplained divine wonder this only works if you have this console log. We need to revisite this later */
    console.log(props.cardsList[cardIndex--].id, 'scrollRight')

    emit('clicked', props.cardsList[cardIndex--].id)
}

const getInitialPosition = computed(() => {
	if (props.dashboardWidgetAnchor) return getMobileAppState.value ? 82 : 152
	else if (props.dashboardWidget) return getMobileAppState.value ? 82 : 111
	else return 92
})

const getCardSizeToMove = computed(() => {
	if (props.dashboardWidgetAnchor) return getMobileAppState.value ? 120 : 226
	else if (props.dashboardWidget) return getMobileAppState.value ? 120 : 160
	else return 134
})

const translateCss = computed(() => {
    const initialPosition = getInitialPosition.value
	const cardSizeToMove = getCardSizeToMove.value

    let selectedIndex
    let value

    props.cardsList.forEach((obj, index) => {
        if (obj.id === props.selectedCardId) selectedIndex = index
    })
    /*
    * initialPosition -> Card Selected -+ width/2
    * cardSizeToMove -> Card non selected width
    * selectedIndex * 0.5 -> magic number to compensate our translates
    */
    switch (selectedIndex) {
    case 0 : value = initialPosition
        break
    default: value = initialPosition + selectedIndex + selectedIndex * cardSizeToMove - selectedIndex * 0.5
    }

    const translateCss = {
        '-webkit-transform': `translateX(calc(50% - ${value}px))`,
        '-ms-transform': `translateX(calc(50% - ${value}px))`,
        '-o-transform': `translateX(calc(50% - ${value}px))`,
        transform: `translateX(calc(50% - ${value}px))`
    }

    return translateCss
})

const overflowWrapperRef = ref('')
const hidePreviousCards = ref(false)
const checkComponentSize = () => {
	hidePreviousCards.value = !isCompanySession.value && overflowWrapperRef.value.clientWidth <= 660
}

onMounted(() => {
    checkComponentSize()
    window.addEventListener('resize', checkComponentSize)
})

onBeforeUnmount(() => {
    window.removeEventListener('resize', checkComponentSize)
})
</script>

<style lang="scss" scoped>
.overflow-hidden-wrapper {
    width: 100%;
    height: fit-content;

    overflow: hidden;
    position: relative;

	.button-add {
		width: 48px;
		height: 68px;

		position: absolute;
		top: calc(208px / 2);
		left: 36px;
		transform: translateY(-50%);
		z-index: 2;

		@include H8-Regular;
		color: var(--secondary-dark-2);

		cursor: pointer;

		button {
			width: 48px;
			height: 48px;
			margin-bottom: 8px;

			display: flex;
			align-items: center;
			justify-content: center;

			border-radius: 8px;
			border: dashed 1px var(--secondary-dark-2);
			cursor: pointer;

			svg {
				:deep(.change-color-fill) {
					fill: var(--secondary-dark-2);
				}
			}
		}

		@media (hover: hover) and (pointer: fine) {
			&:hover {
				color: var(--secondary-dark-3);

				button {
					border: dashed 1px var(--secondary-dark-3);
				}
			}
        }
	}

    .icon-wrapper {
        width: 64px;
        height: 128px;

        display: flex;
        align-items: center;

        top: 0;
		position: absolute;
		z-index: 3;

		transition: all .3s cubic-bezier(.25,.8,.5,1);

		&.left {
			left: 0;
            justify-content: flex-start;

            background: var(--gradient-left-fade);

			.tertiary-button-wrapper {
				margin-left: 16px;
			}
		}

		&.right {
			right: 0;
            justify-content: flex-end;

            background: var(--gradient-right-fade);

			.tertiary-button-wrapper {
				margin-right: 16px;
			}
		}

		&.appear-enter-active, &.appear-leave-active{
			opacity: 1;
		}

		&.appear-enter-from, &.appear-leave-to{
			opacity: 0;
		}

	}

    .card-slider-container {
        width: 100%;
        height: 162px;
        position: relative;
        padding-top: 16px;

        transition: all 1s linear;
        z-index: 1;

        .card-slider {
            height: 112px; /* Card selected heigh */

            display: flex;
            flex-direction: row;
            justify-content: flex-start;
            align-items: center;

            transform: translateX(calc(50% - 92px)); // start position for transform
            transition: transform .3s ease-in-out;
        }
    }

	&.bigger-slider, &.medium-slider {
		&:not(.no-before) {
			&:before {
				content: '';
				width: 170px;
				height: 208px;

				position: absolute;
				background: var(--gradient-left-anchor-fade);
				z-index: 2;
			}
		}

		.icon-wrapper {
			height: 208px; /* Card selected heigh */

			&.left {
				left: 120px;
				background: transparent;
			}

			&.right {
				background: var(--gradient-right-anchor-fade);
			}
		}

		.card-slider-container {
			height: 238px;
			z-index: 1;

			.card-slider {
				height: 188px; /* Card selected heigh */

				transform: translateX(calc(50% - 152px)); // start position for transform
			}
		}

		&.mobile {
			.button-add {
				top: calc(102px / 2);
				left: 16px;
			}

			.card-slider-container {
				height: 136px;
				z-index: 1;

				.card-slider {
					height: 102px; /* Card selected heigh */

					transform: translateX(calc(50% - 82px)); // start position for transform
				}
			}
		}

		&.is-company-session {
			.icon-wrapper {
				&.left {
					left: 0;
				}
			}
		}
	}

	&.medium-slider {
		&:not(.no-before) {
			&:before {
				height: 170px;
				background: var(--gradient-left-add-button-fade);
			}
		}

		.button-add {
			top: calc(136px / 2);
		}

		.icon-wrapper {
			height: 136px; /* Card selected heigh */

			&.left {
				left: 120px;
			}

			&.right {
				background: var(--gradient-right-fade);
			}
		}

		.card-slider-container {
			height: 186px;
			z-index: 1;

			.card-slider {
				height: 136px; /* Card selected heigh */

				transform: translateX(calc(50% - 82px)); // start position for transform
			}
		}
	}
}
</style>
